import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, PlusIcon } from "lucide-react";

import type { SkillView } from "../../state/connections";
import { Button } from "../ui/button";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { CatalogueFilters } from "./CatalogueFilters";
import { CatalogueHeader } from "./CatalogueHeader";
import { SkillRow } from "./SkillRow";
import { SkillsGuide } from "./SkillsGuide";
import { SkillDialog } from "./SkillDetailsDialog";
import {
  filterSkills,
  SKILL_FILTERS,
  SKILL_FILTER_DESCRIPTION,
  type SkillFilter,
} from "./skillContent";

/** One searchable library: availability is distinct from a skill running in chat. */
export function SkillsTab({
  skills,
  searchQuery,
  isPending,
  available,
  onCreateSkill,
  onSaved,
  orgSegment,
}: {
  readonly skills: ReadonlyArray<SkillView>;
  readonly searchQuery: string;
  readonly isPending: boolean;
  readonly available: boolean;
  readonly onCreateSkill?: () => void;
  readonly onSaved?: () => void;
  /** The organization the page acts for; links to its skill leaderboard. */
  readonly orgSegment?: string | null;
}) {
  const [activeFilter, setActiveFilter] = useState<SkillFilter>("all");
  const filtered = useMemo(
    () => filterSkills(skills, activeFilter, searchQuery),
    [skills, activeFilter, searchQuery],
  );
  const query = searchQuery.trim();

  return (
    <SkillDialog onSaved={onSaved}>
      {(openSkill) => (
        <div>
          <CatalogueHeader
            title="Skills"
            description="Reusable instructions for writing documents, reviewing evidence and following your team's process."
            action={
              <div className="flex items-center gap-2">
                {orgSegment ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Skills ranked by how often your chats use them"
                    render={<Link to="/$org/skills" params={{ org: orgSegment }} />}
                  >
                    Leaderboard
                    <ArrowUpRightIcon aria-hidden />
                  </Button>
                ) : null}
                {available && onCreateSkill ? (
                  <Button
                    size="sm"
                    variant="outline"
                    title="Draft a skill in chat"
                    onClick={onCreateSkill}
                  >
                    <PlusIcon aria-hidden />
                    Create skill
                  </Button>
                ) : null}
              </div>
            }
          />
          <SkillsGuide />
          {isPending && skills.length === 0 ? (
            <ListSkeleton className="mt-6" label="Loading skills" />
          ) : !available ? (
            <p className="mt-6 text-sm text-muted-foreground">
              This server isn't configured to manage skills.
            </p>
          ) : (
            <section className="mt-6" aria-label="Skill library">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
                <CatalogueFilters
                  label="Filter skills"
                  options={SKILL_FILTERS}
                  value={activeFilter}
                  onChange={setActiveFilter}
                />
                <span role="status" className="text-xs tabular-nums text-muted-foreground">
                  {filtered.length} {filtered.length === 1 ? "skill" : "skills"}
                </span>
              </div>
              <p className="mt-3 text-xs leading-5 text-muted-foreground">
                {SKILL_FILTER_DESCRIPTION[activeFilter]}
              </p>
              {filtered.length > 0 ? (
                <ul className="mt-2 grid grid-cols-1 gap-x-10 lg:grid-cols-2">
                  {filtered.map((skill) => (
                    <li key={skill.id} className="min-w-0 border-b border-border/40">
                      <SkillRow skill={skill} onOpen={openSkill} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-8 text-sm text-muted-foreground">
                  {query
                    ? `No skills match “${query}” in this view. Try another search or filter.`
                    : activeFilter === "organization"
                      ? "No organisation skills yet. Ask an administrator to save a team process as a skill."
                      : "No skills in this view yet. Try another filter."}
                </p>
              )}
            </section>
          )}
        </div>
      )}
    </SkillDialog>
  );
}
